import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppState, UserRole, CareModule, FormData } from '../types';
import { AVAILABLE_MODULES } from '../types/modules';

interface AppStore extends AppState {
  // Sélection de modules pour le mode manuel
  selectedModules: string[];
  setSelectedModules: (modules: string[]) => void;
  toggleModule: (moduleId: string) => void;
  resetModuleSelection: () => void;
  // Actions existantes
  setCurrentRole: (role: UserRole | null) => void;
  setCurrentModule: (module: CareModule | null) => void;
  updateFormData: (data: Partial<FormData>) => void;
  clearFormData: () => void;
  setGeneratedNote: (note: string | null) => void;
  setIsGeneratingNote: (isGenerating: boolean) => void;
  resetState: () => void;
}

const getDefaultModules = () =>
  AVAILABLE_MODULES.filter((module) => module.required).map((module) => module.id);

const initialState: AppState & { selectedModules: string[] } = {
  currentRole: null,
  currentModule: null,
  formData: {},
  isDirty: false,
  isGeneratingNote: false,
  generatedNote: null,
  selectedModules: getDefaultModules(),
};

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Actions pour la sélection de modules
      setSelectedModules: (modules) => {
        set({ selectedModules: modules });
      },

      toggleModule: (moduleId) => {
        const current = get().selectedModules;
        const newModules = current.includes(moduleId)
          ? current.filter((id) => id !== moduleId)
          : [...current, moduleId];
        set({ selectedModules: newModules });
      },

      resetModuleSelection: () => {
        set({
          selectedModules: getDefaultModules(),
          formData: {},
          isDirty: false,
          generatedNote: null,
        });
      },

      setCurrentRole: (role) => {
        set({ currentRole: role });
      },

      setCurrentModule: (module) => {
        set({
          currentModule: module,
          formData: {},
          isDirty: false,
          generatedNote: null,
        });
      },

      updateFormData: (data) => {
        const currentData = get().formData;
        set({
          formData: { ...currentData, ...data },
          isDirty: true,
        });
      },

      clearFormData: () => {
        set({
          formData: {},
          isDirty: false,
          generatedNote: null,
        });
      },

      setGeneratedNote: (note) => {
        set({ generatedNote: note });
      },

      setIsGeneratingNote: (isGenerating) => {
        set({ isGeneratingNote: isGenerating });
      },

      resetState: () => {
        set(initialState);
      },
    }),
    {
      name: 'clara-app-form',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persister les données de formulaire et le module courant
        formData: state.formData,
        currentModule: state.currentModule,
        currentRole: state.currentRole,
        isDirty: state.isDirty,
        // Persister la sélection de modules
        selectedModules: state.selectedModules,
        // Ne pas persister: generatedNote, isGeneratingNote (états transitoires)
      }),
      version: 1,
    }
  )
);
